import {useState, useEffect, useMemo} from "react"
import {useDebounce} from "@/hooks/useDebounce"
import {useSearchThreads} from "@/utils/graphQL/queries/threads/useSearchThreads"
import {ThreadWithProfiles} from "@/utils/graphQL/queries/threads/shared"
import {OptionMap} from "@/components/archive/Filtering/utils"
import {ITEMS_PER_PAGE} from "@/constants"
import {extractParticipantProfileIds} from "@/features/archive-browser/archiveUtils"
import {useViewModeManager} from "./useViewModeManager"
import {ViewMode} from "../archiveTypes"

interface UseArchiveSearchProps {
  itemsPerPage?: number
}

interface UseArchiveSearchReturn {
  // State
  searchTerm: string
  setSearchTerm: React.Dispatch<React.SetStateAction<string>>
  view: ViewMode
  filters: OptionMap[]
  searchPage: number

  // Search results and loading states
  searchThreads: ThreadWithProfiles[]
  searchLoading: boolean
  searchHasMore: boolean
  searchError: any
  totalCount: number

  // Filter-related values
  dateRanges: {start: string; end: string}[]
  participantProfileIds: string[]

  // Actions
  handleFiltersChange: (newFilters: OptionMap[]) => void
  handleClearAllFilters: () => void
  loadMoreSearchResults: () => void
  setSearchPage: React.Dispatch<React.SetStateAction<number>>
}

/**
 * Hook for managing archive search functionality
 *
 * Provides:
 * - Search term state management with debouncing
 * - Filters for date ranges and participants
 * - View switching between month view and search results (via useViewModeManager)
 * - Pagination with infinite scrolling support
 */
export const useArchiveSearch = ({
  itemsPerPage = ITEMS_PER_PAGE,
}: UseArchiveSearchProps = {}): UseArchiveSearchReturn => {
  // Search term state with debouncing
  const [searchTerm, setSearchTerm] = useState<string>("")
  const debouncedSearchTerm = useDebounce(searchTerm, 500)

  // Pagination state
  const [searchPage, setSearchPage] = useState(0)

  // Filters state
  const [filters, setFilters] = useState<OptionMap[]>([])

  // Extract date ranges from filters
  const dateRanges = useMemo(
    () =>
      filters
        .filter((f) => f.type === "date_range")
        .map((f) => ({
          start: f.value.start,
          end: f.value.end,
        })),
    [filters]
  )

  // Extract participant profile IDs from filters
  const participantProfileIds = useMemo(
    () => extractParticipantProfileIds(filters),
    [filters]
  )

  // Manage view mode switching
  const viewModeManager = useViewModeManager({
    hasSearchTerm: !!debouncedSearchTerm,
    hasDateFilters: dateRanges.length > 0,
    hasParticipantFilters: participantProfileIds.length > 0,
  })

  // Use the search threads hook with pagination
  const {
    threads: searchThreads,
    currentPageThreads,
    isLoading: searchLoading,
    hasMore: searchHasMore,
    error: searchError,
    page: currentSearchPage,
    totalCount: searchTotalCount,
  } = useSearchThreads(
    debouncedSearchTerm || undefined,
    dateRanges.length > 0 ? dateRanges : undefined,
    // Ensure participantProfileIds is undefined when empty
    participantProfileIds && participantProfileIds.length > 0
      ? participantProfileIds
      : undefined,
    searchPage,
    itemsPerPage
  )

  // Reset search page when switching to search mode
  useEffect(() => {
    if (viewModeManager.isSearchMode) {
      setSearchPage(0)
    }
  }, [viewModeManager.isSearchMode])

  // Handle updating filters
  const handleFiltersChange = (newFilters: OptionMap[]) => {
    // Reset search page when filters change
    setSearchPage(0)
    setFilters(newFilters)
  }

  // Handle clearing all filters
  const handleClearAllFilters = () => {
    // Reset filters array state
    setFilters([])
    // Reset search page to top
    setSearchPage(0)
    // Clear search term
    setSearchTerm("")
    // Reset view back to month view
    viewModeManager.resetToMonthView()
  }

  // Increment search page (for infinite scroll)
  const loadMoreSearchResults = () => {
    if (!searchLoading && searchHasMore) {
      setSearchPage((prev) => prev + 1)
    }
  }

  return {
    // State
    searchTerm,
    setSearchTerm,
    view: viewModeManager.view,
    filters,
    searchPage,

    // Search results and loading states
    searchThreads,
    searchLoading,
    searchHasMore,
    searchError,
    totalCount: searchTotalCount,

    // Filter-related values
    dateRanges,
    participantProfileIds,

    // Actions
    handleFiltersChange,
    handleClearAllFilters,
    loadMoreSearchResults,
    setSearchPage,
  }
}
